// データセット削除時の依存関係チェック用ユーティリティ

import type { MIDASProject } from '../../types/project.types';
import type { ReportElementUnion } from '../../types/report.types';
import type { DatasetId } from '../../types';
import type { LayoutState } from '../../types/pane.types';
import { ModelManager } from './ModelManager';

export interface DatasetDependencies {
  models: Array<{
    id: string;
    name: string;
    type: string;
  }>;
  reports: Array<{
    id: string;
    name: string;
    elements: Array<{
      id: string;
      type: string;
    }>;
  }>;
  derivedDatasets: Array<{
    id: string;
    name: string;
  }>;
  tabs: Array<{
    id: string;
    title: string;
    type: string;
  }>;
  hasAnyDependencies: boolean;
}

/**
 * データセットの依存関係をチェックする
 * @param project MIDASプロジェクト
 * @param datasetId 削除対象のデータセットID
 * @param layoutState オプション: 現在のレイアウト状態（タブの依存チェックに使用）
 * @returns 依存関係の詳細
 */
export function checkDatasetDependencies(
  project: MIDASProject,
  datasetId: DatasetId,
  layoutState?: LayoutState
): DatasetDependencies {
  const dependencies: DatasetDependencies = {
    models: [],
    reports: [],
    derivedDatasets: [],
    tabs: [],
    hasAnyDependencies: false
  };

  // 1. モデルの依存関係をチェック
  const modelManager = new ModelManager(project);

  // トレーニングデータセットとして使用されているモデル
  const dependentModels = modelManager.getModelsByDataset(datasetId);

  // 診断データセットとして参照されているモデルもチェック
  const allModels = modelManager.getAllModels();
  for (const model of allModels) {
    if (model.type === 'glm') {
      const glmParameters = model.parameters as import('../../types/model.types').GLMParameters;
      if (glmParameters.diagnosticSummary?.diagnosticDatasetId === datasetId) {
        // このモデルの診断データセットとして使用されている
        if (!dependentModels.find(m => m.id === model.id)) {
          dependentModels.push(model);
        }
      }
    }
  }

  dependencies.models = dependentModels.map(model => ({
    id: model.id,
    name: model.name,
    type: model.type
  }));

  // 2. レポートの依存関係をチェック
  if (project.reports && Array.isArray(project.reports)) {
    for (const report of project.reports) {
      const dependentElements: Array<{ id: string; type: string }> = [];

      for (const element of Object.values(report.elements)) {
        if (isElementDependentOnDataset(element, datasetId)) {
          dependentElements.push({
            id: element.id,
            type: element.type
          });
        }
      }

      if (dependentElements.length > 0) {
        dependencies.reports.push({
          id: report.id,
          name: report.name,
          elements: dependentElements
        });
      }
    }
  }

  // 3. 派生データセットの依存関係をチェック
  if (project.datasets) {
    for (const [id, dataset] of Object.entries(project.datasets)) {
      if (dataset.type === 'derived' && dataset.parentDatasetIds.includes(datasetId)) {
        dependencies.derivedDatasets.push({
          id,
          name: dataset.name
        });
      }
    }
  }

  // 4. タブの依存関係をチェック
  if (layoutState) {
    const allTabs = getAllTabsFromLayout(layoutState);
    for (const tab of allTabs) {
      if (isTabDependentOnDataset(tab, datasetId)) {
        dependencies.tabs.push({
          id: tab.id,
          title: tab.title,
          type: tab.type
        });
      }
    }
  }

  // 依存関係があるかどうかを判定
  dependencies.hasAnyDependencies =
    dependencies.models.length > 0 ||
    dependencies.reports.length > 0 ||
    dependencies.derivedDatasets.length > 0 ||
    dependencies.tabs.length > 0;

  return dependencies;
}

/**
 * レイアウトから全タブを取得する
 * @param layoutState レイアウト状態
 * @returns 全タブの配列
 */
function getAllTabsFromLayout(layoutState: LayoutState): import('../../types/pane.types').TabState[] {
  const tabs: import('../../types/pane.types').TabState[] = [];

  function traversePane(pane: import('../../types/pane.types').PaneNode) {
    if (pane.type === 'container') {
      tabs.push(...pane.tabs);
    } else if (pane.type === 'split') {
      traversePane(pane.leftChild);
      traversePane(pane.rightChild);
    }
  }

  traversePane(layoutState.rootPane);
  return tabs;
}

/**
 * タブがデータセットに依存しているかチェック
 * @param tab タブ
 * @param datasetId データセットID
 * @returns 依存している場合はtrue
 */
function isTabDependentOnDataset(tab: import('../../types/pane.types').TabState, datasetId: DatasetId): boolean {
  if (!tab.metadata) {
    return false;
  }

  // metadata.datasetId
  if (tab.metadata.datasetId === datasetId) {
    return true;
  }

  // metadata.targetDatasetId
  if (tab.metadata.targetDatasetId === datasetId) {
    return true;
  }

  // metadata.initialDatasetId
  if (tab.metadata.initialDatasetId === datasetId) {
    return true;
  }

  // metadata.ephemeralDataset.metadata.sourceDatasetId
  if (tab.metadata.ephemeralDataset?.metadata?.sourceDatasetId === datasetId) {
    return true;
  }

  return false;
}

/**
 * レポート要素がデータセットに依存しているかチェック
 * @param element レポート要素
 * @param datasetId データセットID
 * @returns 依存している場合はtrue
 */
function isElementDependentOnDataset(element: ReportElementUnion, datasetId: DatasetId): boolean {
  switch (element.type) {
    case 'statistics_summary':
      // 統計サマリー要素はdatasetIdを持つかチェック
      return 'datasetId' in element && element.datasetId === datasetId;

    case 'data_table':
      // データテーブル要素はdatasetIdを持つかチェック
      return 'datasetId' in element && element.datasetId === datasetId;

    case 'cross_dataset_comparison':
      // 複数データセット比較要素はdatasetIdsを持つかチェック
      return 'datasetIds' in element && Array.isArray(element.datasetIds) && element.datasetIds.includes(datasetId);

    case 'graph_builder':
    case 'crosstab':
    case 'qq_plot':
      // これらの要素はdatasetIdを持つかチェック
      return 'datasetId' in element && element.datasetId === datasetId;

    default:
      // 未知の要素タイプは安全のためtrueを返す
      return true;
  }
}

/**
 * 依存関係の警告メッセージを生成する
 * @param dependencies 依存関係情報
 * @param datasetName データセット名
 * @returns 警告メッセージ
 */
export function generateDependencyWarningMessage(
  dependencies: DatasetDependencies,
  datasetName: string
): string {
  if (!dependencies.hasAnyDependencies) {
    return '';
  }

  const warnings: string[] = [];
  
  warnings.push(`Dataset "${datasetName}" is referenced by:`);
  warnings.push('');

  if (dependencies.models.length > 0) {
    warnings.push(`Models (${dependencies.models.length}):`);
    dependencies.models.forEach(model => {
      warnings.push(`  • ${model.name} (${model.type})`);
    });
    warnings.push('');
  }

  if (dependencies.reports.length > 0) {
    warnings.push(`Reports (${dependencies.reports.length}):`);
    dependencies.reports.forEach(report => {
      warnings.push(`  • ${report.name} (${report.elements.length} elements)`);
    });
    warnings.push('');
  }

  if (dependencies.derivedDatasets.length > 0) {
    warnings.push(`Derived Datasets (${dependencies.derivedDatasets.length}):`);
    dependencies.derivedDatasets.forEach(dataset => {
      warnings.push(`  • ${dataset.name}`);
    });
    warnings.push('');
  }

  if (dependencies.tabs.length > 0) {
    warnings.push(`Open Tabs (${dependencies.tabs.length}):`);
    dependencies.tabs.forEach(tab => {
      warnings.push(`  • ${tab.title} (${tab.type})`);
    });
    warnings.push('');
  }

  warnings.push('Deleting this dataset will break these references.');
  warnings.push('Are you sure you want to continue?');

  return warnings.join('\n');
}

// ===== レポートの依存関係チェック =====

export interface ReportDependencies {
  tabs: Array<{
    id: string;
    title: string;
    type: string;
  }>;
  hasAnyDependencies: boolean;
}

/**
 * レポートの依存関係をチェックする
 */
export function checkReportDependencies(
  reportId: string,
  layoutState?: LayoutState
): ReportDependencies {
  const dependencies: ReportDependencies = {
    tabs: [],
    hasAnyDependencies: false
  };

  if (layoutState) {
    const allTabs = getAllTabsFromLayout(layoutState);
    for (const tab of allTabs) {
      if (tab.metadata?.reportId === reportId) {
        dependencies.tabs.push({
          id: tab.id,
          title: tab.title,
          type: tab.type
        });
      }
    }
  }

  dependencies.hasAnyDependencies = dependencies.tabs.length > 0;
  return dependencies;
}

/**
 * レポートの依存関係警告メッセージを生成する
 */
export function generateReportDependencyWarningMessage(
  dependencies: ReportDependencies,
  reportName: string
): string {
  if (!dependencies.hasAnyDependencies) {
    return '';
  }

  const warnings: string[] = [];
  warnings.push(`Report "${reportName}" is referenced by:`);
  warnings.push('');

  if (dependencies.tabs.length > 0) {
    warnings.push(`Open Tabs (${dependencies.tabs.length}):`);
    dependencies.tabs.forEach(tab => {
      warnings.push(`  • ${tab.title} (${tab.type})`);
    });
    warnings.push('');
  }

  warnings.push('Deleting this report will close these tabs.');
  warnings.push('Are you sure you want to continue?');

  return warnings.join('\n');
}

// ===== モデルの依存関係チェック =====

export interface ModelDependencies {
  derivedDatasets: Array<{
    id: string;
    name: string;
  }>;
  tabs: Array<{
    id: string;
    title: string;
    type: string;
  }>;
  hasAnyDependencies: boolean;
}

/**
 * モデルの依存関係をチェックする
 */
export function checkModelDependencies(
  project: MIDASProject,
  modelId: string,
  layoutState?: LayoutState
): ModelDependencies {
  const dependencies: ModelDependencies = {
    derivedDatasets: [],
    tabs: [],
    hasAnyDependencies: false
  };

  // GLM診断データセットをチェック
  const modelManager = new ModelManager(project);
  const model = modelManager.getModel(modelId);
  if (model && model.type === 'glm') {
    const glmParameters = model.parameters as import('../../types/model.types').GLMParameters;
    const diagnosticDatasetId = glmParameters.diagnosticSummary?.diagnosticDatasetId;
    if (diagnosticDatasetId && project.datasets[diagnosticDatasetId]) {
      dependencies.derivedDatasets.push({
        id: diagnosticDatasetId,
        name: project.datasets[diagnosticDatasetId].name
      });
    }
  }

  // GLM予測でモデルを使用しているDerivedDataSetをチェック
  if (project.datasets) {
    for (const [id, dataset] of Object.entries(project.datasets)) {
      if (dataset.type === 'derived') {
        const operation = dataset.operation;
        if (operation?.type === 'glm_prediction') {
          const op = operation as { modelId?: string };
          if (op.modelId === modelId) {
            dependencies.derivedDatasets.push({
              id,
              name: dataset.name
            });
          }
        }
      }
    }
  }

  // モデルを表示しているタブをチェック
  if (layoutState) {
    const allTabs = getAllTabsFromLayout(layoutState);
    for (const tab of allTabs) {
      // Model Detailタブ、またはsavedModelIdを持つGLMタブ
      if (tab.metadata?.modelId === modelId || tab.metadata?.savedModelId === modelId) {
        dependencies.tabs.push({
          id: tab.id,
          title: tab.title,
          type: tab.type
        });
      }
    }
  }

  dependencies.hasAnyDependencies =
    dependencies.derivedDatasets.length > 0 ||
    dependencies.tabs.length > 0;

  return dependencies;
}

/**
 * モデルの依存関係警告メッセージを生成する
 */
export function generateModelDependencyWarningMessage(
  dependencies: ModelDependencies,
  modelName: string
): string {
  if (!dependencies.hasAnyDependencies) {
    return '';
  }

  const warnings: string[] = [];
  warnings.push(`Model "${modelName}" is referenced by:`);
  warnings.push('');

  if (dependencies.derivedDatasets.length > 0) {
    warnings.push(`Derived Datasets (${dependencies.derivedDatasets.length}):`);
    dependencies.derivedDatasets.forEach(dataset => {
      warnings.push(`  • ${dataset.name}`);
    });
    warnings.push('');
  }

  if (dependencies.tabs.length > 0) {
    warnings.push(`Open Tabs (${dependencies.tabs.length}):`);
    dependencies.tabs.forEach(tab => {
      warnings.push(`  • ${tab.title} (${tab.type})`);
    });
    warnings.push('');
  }

  warnings.push('Deleting this model will break these references.');
  warnings.push('Are you sure you want to continue?');

  return warnings.join('\n');
}